import React, { useState, useEffect } from 'react'
import { Recipient } from '../../../common/types/form'
import StyledLiveModal from './styles/StyleImportSummaryModal'
import i18n from 'live/services/i18n'
import { CsvToRecipientData, ModificationActionSorter, RecipientModificationAction, RecipientModifications } from './helper/types'
import { applyAllModifications, validateModificationAction } from './helper/applyRecipientsImportModifications'
import { onChangeKeppDeleteds, onChangeOverrideUpdateds } from './helper/changeFlagsRecipientImport'
import buildModificationsInfo from './helper/buildModificationsRecipientImport'
import LiveTooltip from 'live/components/ui/tooltip'

interface ModalRecipientsImportSummaryProps {
    currentSavedRecipients: CsvToRecipientData[]
    importedRecipients: CsvToRecipientData[]
    showingSummaryModal: boolean
    onClose: (cancel: boolean) => void
    onSave: (newRecipientList: Recipient[]) => void
}

export function ModalRecipientsImportSummary({
    currentSavedRecipients,
    importedRecipients,
    showingSummaryModal,
    onClose,
    onSave
}: ModalRecipientsImportSummaryProps): JSX.Element {
    const [keepDeletedRecipients, setKeepDeletedRecipients] = useState<boolean>(true)
    const [overrideRecipients, setoverrideRecipients] = useState<boolean>(false)
    const [recipientsModifications, setRecipientsModifications] = useState<RecipientModifications[]>([])

    useEffect(() => {
        const modifications = buildModificationsInfo(currentSavedRecipients, importedRecipients)
        onChangeKeppDeleteds(modifications, keepDeletedRecipients)
        onChangeOverrideUpdateds(modifications, overrideRecipients)

        modifications
            .filter(modification => 
                //when override is set it can put a group without recipients
                modification.action === RecipientModificationAction.OVERRIDED 
            ).forEach(modification => {
                modification.actionErrors = validateModificationAction(modification)
            })

        if (importedRecipients.length > 0) {
            const errors =  modifications
            .map(modification => {
                const errors = [
                    ...modification.errors,
                    ...(modification.actionErrors ?? [])
                ]
                if (errors.length > 0) {
                    return {
                        [modification.groupName]: errors
                    }
                }
                return null
            })
            .filter(obj => !!obj)
            if(errors.length > 0)
                console.debug(
                    'detailed recipient groups import errors: \n',
                    errors
                )
        }
        setRecipientsModifications(modifications)
    }, [importedRecipients])

    useEffect(() => {
        setRecipientsModifications(onChangeKeppDeleteds([ ...recipientsModifications ], keepDeletedRecipients))
    }, [keepDeletedRecipients])

    useEffect(() => {
        setRecipientsModifications(onChangeOverrideUpdateds([ ...recipientsModifications ], overrideRecipients))
    }, [overrideRecipients])

    const qtdErrors = (modifications: RecipientModifications[]): number => {
        return modifications.reduce((acc, cur) => acc + cur.errors.length, 0)
    }

    return (
        <StyledLiveModal
            show={showingSummaryModal}
            onHide={() => onClose(false)}
            animation={false}
            className="modal-sm"
        >
            <StyledLiveModal.Header>
                <StyledLiveModal.Title>{i18n('Import Summary')}</StyledLiveModal.Title>
            </StyledLiveModal.Header>
            <StyledLiveModal.Body className="RecipientImportSummary__body">
                <div className="RecipientImportSummary__body__content">
                    <div className="RecipientImportSummary__body__content__infos">
                        <div className={`RecipientImportSummary__body__content__infos__message-INFO`}>
                            <span>
                                {i18n('These are the updates on the Recipient Groups list. ')}
                                <b>
                                    {recipientsModifications.length}
                                    {i18n(' groups will be imported ')}
                                    {qtdErrors(recipientsModifications) > 0
                                        ? `${i18n('with')} ${qtdErrors(recipientsModifications)} errors.`
                                        : i18n('with no errors.')}
                                </b>
                            </span>
                            <br />
                            <span>
                                {i18n(
                                    'If there is an existing recipient group, it will be updated to its new version, and any duplicity will be ignored.'
                                )}
                            </span>
                        </div>
                        <div className={`RecipientImportSummary__body__content__infos__message-WARN`}>
                            <span>
                                {i18n('Some groups will be deleted if they are not present on the spreadsheet file.')}
                            </span>
                        </div>
                    </div>
                    <div className="RecipientImportSummary__body__content__flags">
                        <div className="RecipientImportSummary__body__content__flags__keepDeleted">
                            <input
                                type="checkbox"
                                checked={keepDeletedRecipients}
                                onChange={() => setKeepDeletedRecipients(!keepDeletedRecipients)}
                            />
                            <span className="RecipientImportSummary__body__content__flags__keepDeleted__text">
                                <i>{i18n("Keep the groups flagged as 'Deleted' on the recipient groups list.")}</i>
                            </span>
                        </div>
                        <div className="RecipientImportSummary__body__content__flags__overrideUpdated">
                            <input
                                type="checkbox"
                                checked={overrideRecipients}
                                onChange={() => setoverrideRecipients(!overrideRecipients)}
                            />
                            <span className="RecipientImportSummary__body__content__flags__flagOverrideUpdateds__text">
                                <i>{i18n("Override the groups flagged as 'Updated' on the recipient group list .")}</i>
                            </span>
                        </div>
                    </div>
                    <div className="RecipientImportSummary__body__content__changesTable">
                        <div className="RecipientImportSummary__body__content__changesTable__header">
                            <div className="RecipientImportSummary__body__content__changesTable__col1">
                                {`${i18n('Group Name')}`.toUpperCase()}
                            </div>
                            <div className="RecipientImportSummary__body__content__changesTable__col2">
                                {`${i18n('Total Recipients')}`.toUpperCase()}
                            </div>
                            <div className="RecipientImportSummary__body__content__changesTable__col3">
                                {`${i18n('New Recipients')}`.toUpperCase()}
                            </div>
                            <div className="RecipientImportSummary__body__content__changesTable__col4">
                                {`${i18n('Errors')}`.toUpperCase()}
                            </div>
                            <div className="RecipientImportSummary__body__content__changesTable__col5">
                                {`${i18n('Action')}`.toUpperCase()}
                            </div>
                        </div>
                        <div className="RecipientImportSummary__body__content__changesTable__body">
                            {recipientsModifications
                                .sort((a, b) => {
                                    const errorsA = [...a.errors, ...(a.actionErrors?? [])]
                                    const errorsB = [...b.errors, ...(b.actionErrors?? [])]
                                    const valA = errorsA.length > 0 ? 100 : ModificationActionSorter[a.action]
                                    const valB = errorsB.length > 0 ? 100 : ModificationActionSorter[b.action]
                                    if (valA == 100 && valB == 100) return a.groupName.localeCompare(b.groupName)
                                    return valB - valA
                                })
                                .map((modification, idx) => {
                                    const currentEmails = [...(modification.currentRecipient?.recipients ?? [])]
                                    const importedEmails = [...(modification.importedRecipient?.recipients ?? [])]
                                    const allRecipients = [...new Set([...currentEmails, ...importedEmails])]
                                    const errors = [...modification.errors, ...(modification.actionErrors ?? [])]
                                    const hasErrors = errors.length !== 0
                                    return (
                                        <div className="RecipientImportSummary__body__content__changesTable__body__row">
                                            <div className="RecipientImportSummary__body__content__changesTable__col1">
                                                {modification.groupName}
                                            </div>
                                            <div className="RecipientImportSummary__body__content__changesTable__col2">
                                                {allRecipients.length}
                                            </div>
                                            <div className="RecipientImportSummary__body__content__changesTable__col3">
                                                {allRecipients.filter(email => !currentEmails.includes(email)).length}
                                            </div>
                                            <div className="RecipientImportSummary__body__content__changesTable__col4">
                                                <div
                                                    className={`RecipientImportSummary__body__content__changesTable__errosNumber${
                                                        hasErrors ? '-withErrors' : ''
                                                    }`}
                                                >
                                                    <span>{errors.length}</span>
                                                    {hasErrors ? (
                                                        <LiveTooltip
                                                            content={
                                                                <div>
                                                                    <span>
                                                                        {errors.filter(
                                                                            msg =>
                                                                                msg.includes('email') ||
                                                                                (msg.includes('e-mail') &&
                                                                                    msg.includes('recipient'))
                                                                        ).length > 0
                                                                            ? i18n('Invalid recipient emails')
                                                                            : null}
                                                                    </span>
                                                                    <br />
                                                                    <span>
                                                                        {errors.filter(msg => msg.includes('lookup'))
                                                                            .length > 0
                                                                            ? i18n('Lookups with error')
                                                                            : null}
                                                                    </span>
                                                                </div>
                                                            }
                                                            position="top"
                                                        >
                                                            <i
                                                                className="far fa-exclamation-circle"
                                                                aria-hidden="true"
                                                            />
                                                        </LiveTooltip>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="RecipientImportSummary__body__content__changesTable__col5">
                                                <span
                                                    className={`RecipientImportSummary__body__content__changesTable__action-${modification.action}`}
                                                >
                                                    {modification.action}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                </div>
            </StyledLiveModal.Body>
            <StyledLiveModal.Footer>
                <button
                    className="btn btn-primary"
                    disabled={qtdErrors(recipientsModifications) > 0}
                    style={{ margin: '5px' }}
                    onClick={() => {
                        onSave(applyAllModifications(recipientsModifications))
                        onClose(false)
                        //maybe place validation?
                    }}
                >
                    {i18n('Confirm')}
                </button>
                <button className="btn btn-secundary" onClick={() => onClose(true)} style={{ margin: '5px' }}>
                    {i18n('Cancel')}
                </button>
            </StyledLiveModal.Footer>
        </StyledLiveModal>
    )
}
